import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import NetAmount from '../../components/shared/NetAmount';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import api from '../../services/api';
import { toast } from 'sonner';
import { formatDateOnly } from '../../utils/timezone';
import { PlayerGamesView } from '../../components/PlayerGamesView';
import {
  ArrowLeft,
  User,
  Calendar,
  GraduationCap,
  Trophy,
  CheckCircle,
  XCircle,
  Clock,
  Pencil,
  ExternalLink,
  Shirt,
  Phone,
  FileText,
  Activity,
  Users,
  MessageSquare,
  Plus,
  Trash2,
  Flag,
  PauseCircle,
  PlayCircle,
  AlertTriangle,
  DollarSign,
  UserPlus,
  Search,
  X,
} from 'lucide-react';

// Player Notes Section Component
const PlayerNotesSection = ({ playerId }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState({ content: '', note_type: 'general', is_flagged: false });
  const [saving, setSaving] = useState(false);

  const fetchNotes = async () => {
    try {
      const response = await api.get(`/players/${playerId}/notes`);
      setNotes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [playerId]);

  const handleAddNote = async () => {
    if (!newNote.content.trim()) {
      toast.error('Please enter note content');
      return;
    }
    
    setSaving(true);
    try {
      await api.post(`/players/${playerId}/notes`, newNote);
      toast.success('Note added successfully');
      setNewNote({ content: '', note_type: 'general', is_flagged: false });
      setShowAddNote(false);
      fetchNotes();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    
    try {
      await api.delete(`/players/${playerId}/notes/${noteId}`);
      toast.success('Note deleted');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleToggleFlag = async (noteId) => {
    try {
      await api.put(`/players/${playerId}/notes/${noteId}/flag`);
      fetchNotes();
    } catch (error) {
      toast.error('Failed to update flag');
    }
  };

  const getNoteTypeBadge = (type) => {
    const colors = {
      general: 'bg-slate-100 text-slate-700',
      performance: 'bg-blue-100 text-blue-700',
      behavior: 'bg-purple-100 text-purple-700',
      medical: 'bg-red-100 text-red-700',
      other: 'bg-gray-100 text-gray-700'
    };
    return <Badge className={`${colors[type] || colors.general} capitalize`}>{type}</Badge>;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Player Notes ({notes.length})
        </CardTitle>
        <Button size="sm" onClick={() => setShowAddNote(!showAddNote)} data-testid="add-note-btn">
          <Plus className="h-4 w-4 mr-1" />
          Add Note
        </Button>
      </CardHeader>
      <CardContent>
        {/* Add Note Form */}
        {showAddNote && (
          <div className="mb-6 p-4 border rounded-lg bg-slate-50">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Note Content *</Label>
                <Textarea
                  value={newNote.content}
                  onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                  placeholder="Enter note about this player..."
                  rows={3}
                  data-testid="note-content-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Note Type</Label>
                  <Select
                    value={newNote.note_type}
                    onValueChange={(v) => setNewNote({ ...newNote, note_type: v })}
                  >
                    <SelectTrigger data-testid="note-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="performance">Performance</SelectItem>
                      <SelectItem value="behavior">Behavior</SelectItem>
                      <SelectItem value="medical">Medical</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newNote.is_flagged}
                      onChange={(e) => setNewNote({ ...newNote, is_flagged: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <span className="text-sm text-slate-600">Flag as important</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAddNote(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddNote} disabled={saving} data-testid="save-note-btn">
                  {saving ? 'Saving...' : 'Save Note'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Notes List */}
        {notes.length > 0 ? (
          <div className="space-y-4">
            {notes.map((note) => (
              <div
                key={note.id}
                className={`p-4 rounded-lg border ${note.is_flagged ? 'bg-amber-50 border-amber-200' : 'bg-white'}`}
                data-testid={`note-${note.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getNoteTypeBadge(note.note_type)}
                      {note.is_flagged && (
                        <Badge className="bg-amber-100 text-amber-700">
                          <Flag className="h-3 w-3 mr-1" />
                          Flagged
                        </Badge>
                      )}
                    </div>
                    <p className="text-slate-700 whitespace-pre-wrap">{note.content}</p>
                    <p className="text-xs text-slate-500 mt-2">
                      By {note.created_by_name} • {new Date(note.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleFlag(note.id)}
                      className={note.is_flagged ? 'text-amber-600' : 'text-slate-400'}
                      title={note.is_flagged ? 'Remove flag' : 'Flag as important'}
                    >
                      <Flag className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteNote(note.id)}
                      className="text-red-500 hover:text-red-700"
                      title="Delete note"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <MessageSquare className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p>No notes for this player yet</p>
            <p className="text-sm mt-2">Click "Add Note" to create the first note.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const PlayerDetail = () => {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [tournamentHistory, setTournamentHistory] = useState([]);
  const [loadingTournaments, setLoadingTournaments] = useState(false);

  // Register for Tournament dialog
  const [registerTournamentDialog, setRegisterTournamentDialog] = useState(false);
  const [activeTournaments, setActiveTournaments] = useState([]);
  const [loadingActiveTournaments, setLoadingActiveTournaments] = useState(false);
  const [selectedTournamentId, setSelectedTournamentId] = useState('');
  const [selectedAgeCategory, setSelectedAgeCategory] = useState('');
  const [registrationEligibility, setRegistrationEligibility] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [overrideEligibility, setOverrideEligibility] = useState(false);
  const [registrationNotes, setRegistrationNotes] = useState('');
  const [registeringForTournament, setRegisteringForTournament] = useState(false);
  
  // Edit dialog
  const [editDialog, setEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [playerForm, setPlayerForm] = useState({});
  
  // Add Parent dialog
  const [addParentDialog, setAddParentDialog] = useState(false);
  const [parentSearch, setParentSearch] = useState('');
  const [allParents, setAllParents] = useState([]);
  const [addingParent, setAddingParent] = useState(false);
  const [removingParent, setRemovingParent] = useState(null);
  
  // Unlink Primary Parent Confirmation
  const [unlinkParentDialog, setUnlinkParentDialog] = useState(false);
  const [unlinkingParent, setUnlinkingParent] = useState(false);
  
  // Enrollment action dialog
  const [enrollmentActionDialog, setEnrollmentActionDialog] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [enrollmentAction, setEnrollmentAction] = useState(null); // 'pause', 'cancel', 'resume'
  const [actionForm, setActionForm] = useState({
    pause_start_date: '',
    resume_date: '',
    cancel_date: '',
    reason: '',
    // Admin overrides applied when cancelling. Both default to off;
    // the dialog wires them to explicit checkboxes so the action is
    // never silent. Persisted on the cancel payload.
    override_notice_period: false,
    override_credit: false,
  });
  const [creditCalculation, setCreditCalculation] = useState(null);
  const [calculatingCredit, setCalculatingCredit] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);

  useEffect(() => {
    fetchPlayerDetails();
  }, [playerId]);

  useEffect(() => {
    // Fetch tournament history when tab changes to tournaments
    if (activeTab === 'tournaments' && tournamentHistory.length === 0) {
      fetchTournamentHistory();
    }
  }, [activeTab]);

  const fetchTournamentHistory = async () => {
    setLoadingTournaments(true);
    try {
      const response = await api.get(`/tournaments/player/${playerId}/history`);
      setTournamentHistory(response.data || []);
    } catch (error) {
      console.error('Failed to load tournament history:', error);
      setTournamentHistory([]);
    } finally {
      setLoadingTournaments(false);
    }
  };

  // ---- Register player for a tournament (admin / staff) ----
  // Opens the dialog and pulls the active tournament list so the admin can
  // pick one without leaving the player profile. Past tournaments are
  // already excluded server-side by `calculate_tournament_status`.
  const openRegisterTournamentDialog = async () => {
    setSelectedTournamentId('');
    setSelectedAgeCategory('');
    setRegistrationEligibility(null);
    setOverrideEligibility(false);
    setRegistrationNotes('');
    setRegisterTournamentDialog(true);
    setLoadingActiveTournaments(true);
    try {
      const res = await api.get('/tournaments');
      // Only surface tournaments that are open for registration (status=active)
      // or upcoming (status=future). Drop completed/past tournaments.
      const open = (res.data || []).filter(t => t.status === 'active' || t.status === 'future');
      setActiveTournaments(open);
    } catch (e) {
      console.error('Failed to load tournaments:', e);
      setActiveTournaments([]);
      toast.error('Failed to load tournaments');
    } finally {
      setLoadingActiveTournaments(false);
    }
  };

  // When tournament + age category are both chosen, ping the eligibility
  // endpoint so the admin sees ahead-of-time whether they'll need an
  // override. Failing the network call is non-fatal; the registration
  // endpoint also re-checks on the server.
  const checkEligibility = async (tournamentId, category) => {
    if (!tournamentId || !category) {
      setRegistrationEligibility(null);
      return;
    }
    setCheckingEligibility(true);
    try {
      const res = await api.get(`/tournaments/${tournamentId}/check-eligibility/${playerId}`, {
        params: { category }
      });
      setRegistrationEligibility(res.data);
      // Default the override checkbox based on the eligibility result so
      // an in-bounds player doesn't trigger an override and an out-of-
      // bounds player's submit isn't blocked silently.
      setOverrideEligibility(!res.data?.is_eligible);
    } catch (e) {
      console.error('Eligibility check failed:', e);
      setRegistrationEligibility(null);
    } finally {
      setCheckingEligibility(false);
    }
  };

  const handleTournamentChange = (tournamentId) => {
    setSelectedTournamentId(tournamentId);
    setSelectedAgeCategory('');
    setRegistrationEligibility(null);
    setOverrideEligibility(false);
  };

  const handleCategoryChange = (category) => {
    setSelectedAgeCategory(category);
    if (selectedTournamentId && category) {
      checkEligibility(selectedTournamentId, category);
    }
  };

  const submitTournamentRegistration = async () => {
    if (!selectedTournamentId || !selectedAgeCategory) {
      toast.error('Please pick a tournament and age category');
      return;
    }
    // Block submit if player is ineligible AND admin hasn't acknowledged
    // the override — the backend will also reject this, but failing fast in
    // the UI gives a cleaner error message.
    if (registrationEligibility && !registrationEligibility.is_eligible && !overrideEligibility) {
      toast.error('Player is not eligible for this category — confirm override to register');
      return;
    }
    setRegisteringForTournament(true);
    try {
      const payload = {
        player_id: playerId,
        age_category: selectedAgeCategory,
        can_travel: true,
        availability_status: 'yes',
        available_dates: [],
        unavailable_dates: [],
        notes: registrationNotes || null,
        parent_consent: true,  // Admin records consent on parent's behalf
      };
      const url = `/tournaments/${selectedTournamentId}/admin-register${overrideEligibility ? '?override_eligibility=true' : ''}`;
      const res = await api.post(url, payload);
      const warning = res.data?.warning;
      if (warning) toast.warning(warning);
      toast.success('Player registered for tournament');
      setRegisterTournamentDialog(false);
      // Refresh the tournament history table so the admin immediately
      // sees the new row.
      fetchTournamentHistory();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === 'object' && detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error(detail || 'Failed to register player');
      }
    } finally {
      setRegisteringForTournament(false);
    }
  };

  const fetchPlayerDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/players/${playerId}/details`);
      setPlayer(response.data);
      setPlayerForm({
        full_name: response.data.full_name || '',
        date_of_birth: response.data.date_of_birth || '',
        gender: response.data.gender || '',
        skill_level: response.data.skill_level || 'beginner',
        medical_notes: response.data.medical_notes || '',
        shirt_name: response.data.shirt_name || '',
        shirt_number: response.data.shirt_number || '',
        chest_size: response.data.chest_size || '',
        shirt_length: response.data.shirt_length || '',
        sleeves: response.data.sleeves || '',
        waist_size: response.data.waist_size || '',
        trouser_length: response.data.trouser_length || '',
        cricclubs_id: response.data.cricclubs_id || '',
        cricclubs_email: response.data.cricclubs_email || '',
        player_mobile: response.data.player_mobile || '',
        player_email: response.data.player_email || ''
      });
    } catch (error) {
      toast.error('Failed to load player details');
      navigate('/admin/players');
    } finally {
      setLoading(false);
    }
  };

  // Fetch users (any role) for the Add Parent dialog. Uses the duplicate
  // management user search endpoint so admins can link customers, coaches,
  // staff, admins or other parents — not just role=parent. Customers get
  // auto-promoted to parent on link (handled server-side).
  const fetchParents = async (q = '') => {
    try {
      if (!q || q.trim().length < 2) {
        setAllParents([]);
        return;
      }
      const response = await api.get('/admin/duplicates/users/search', {
        params: { q: q.trim(), role: 'all', limit: 25 },
      });
      setAllParents(response.data?.results || []);
    } catch (error) {
      console.error('Failed to search users:', error);
    }
  };

  // Debounce search-as-you-type
  useEffect(() => {
    if (!addParentDialog) return;
    const t = setTimeout(() => fetchParents(parentSearch), 250);
    return () => clearTimeout(t);
  }, [parentSearch, addParentDialog]);

  // Open Add Parent dialog
  const openAddParentDialog = () => {
    setParentSearch('');
    setAllParents([]);
    setAddParentDialog(true);
  };

  // Add a parent to this player
  const handleAddParent = async (parentId) => {
    setAddingParent(true);
    try {
      await api.post('/admin/duplicates/players/add-parent', {
        player_id: playerId,
        parent_id: parentId
      });
      toast.success('Parent added successfully');
      setAddParentDialog(false);
      fetchPlayerDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add parent');
    } finally {
      setAddingParent(false);
    }
  };

  // Remove a parent from this player
  const handleRemoveParent = async (parentId) => {
    setRemovingParent(parentId);
    try {
      await api.delete(`/admin/duplicates/players/${playerId}/parents/${parentId}`);
      toast.success('Parent removed');
      fetchPlayerDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove parent');
    } finally {
      setRemovingParent(null);
    }
  };

  // Promote an additional parent to be the primary parent.
  const [promotingParent, setPromotingParent] = useState(null);
  const handleMakePrimary = async (parentId, parentName) => {
    if (!window.confirm(
      `Make ${parentName || 'this parent'} the primary parent? ` +
      `${player?.parent_name ? `${player.parent_name} will be moved to additional parents.` : ''}`
    )) return;
    setPromotingParent(parentId);
    try {
      const { data } = await api.post(`/admin/players/${playerId}/make-primary-parent`, {
        parent_id: parentId,
      });
      toast.success(data.message || 'Primary parent updated');
      fetchPlayerDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to make primary');
    } finally {
      setPromotingParent(null);
    }
  };
  
  // Unlink primary parent from player
  const handleUnlinkPrimaryParent = async () => {
    setUnlinkingParent(true);
    try {
      await api.delete(`/admin/players/${playerId}/unlink-parent`);
      toast.success('Parent unlinked successfully. Player no longer has a primary parent.');
      setUnlinkParentDialog(false);
      fetchPlayerDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlink parent');
    } finally {
      setUnlinkingParent(false);
    }
  };

  // Filter results — exclude already-linked guardians. Search itself is
  // server-side now; we no longer filter client-side by name/email.
  const getFilteredParents = () => {
    const currentParentIds = new Set();
    if (player?.parent_id) currentParentIds.add(player.parent_id);
    if (player?.parent_ids) player.parent_ids.forEach(id => currentParentIds.add(id));
    return allParents
      .filter(p => !currentParentIds.has(p.id))
      .slice(0, 25);
  };

  const handleSavePlayer = async () => {
    try {
      setSaving(true);
      
      // Ensure numeric fields are properly typed
      const formData = {
        ...playerForm,
        chest_size: playerForm.chest_size ? parseInt(playerForm.chest_size) || null : null,
        waist_size: playerForm.waist_size ? parseInt(playerForm.waist_size) || null : null,
        shirt_number: playerForm.shirt_number !== '' && playerForm.shirt_number !== null 
          ? parseInt(playerForm.shirt_number) 
          : null,
        shirt_length: playerForm.shirt_length ? parseFloat(playerForm.shirt_length) || null : null,
        trouser_length: playerForm.trouser_length ? parseFloat(playerForm.trouser_length) || null : null,
      };
      
      await api.put(`/players/${playerId}`, formData);
      toast.success('Player updated successfully');
      setEditDialog(false);
      fetchPlayerDetails();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update player';
      toast.error(errorMsg);
      console.error('Update player error:', error.response?.data || error);
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      present: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      absent: { color: 'bg-red-100 text-red-700', icon: XCircle },
      late: { color: 'bg-amber-100 text-amber-700', icon: Clock },
      excused: { color: 'bg-blue-100 text-blue-700', icon: FileText }
    };
    const config = statusConfig[status] || statusConfig.present;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} capitalize`}>
        <Icon className="h-3 w-3 mr-1" />
        {status}
      </Badge>
    );
  };

  const getEnrollmentStatusBadge = (status) => {
    const colors = {
      current: 'bg-green-100 text-green-700',
      approved: 'bg-blue-100 text-blue-700',
      pending: 'bg-amber-100 text-amber-700',
      paused: 'bg-orange-100 text-orange-700',
      cancelled: 'bg-red-100 text-red-700',
      completed: 'bg-slate-100 text-slate-700'
    };
    return (
      <Badge className={`${colors[status] || colors.pending} capitalize`}>
        {status}
      </Badge>
    );
  };

  // Enrollment action handlers
  const openEnrollmentAction = (enrollment, action) => {
    setSelectedEnrollment(enrollment);
    setEnrollmentAction(action);
    setCreditCalculation(null);
    
    // Set default dates
    const today = new Date().toISOString().split('T')[0];

    setActionForm({
      pause_start_date: today,
      // iter334az — Do NOT pre-populate resume_date. Empty default
      // lets admin choose: leave blank for indefinite pause, or fill
      // in a future/past date. Backdating is intentionally allowed —
      // the backend + calculatePauseCredit compute credit based on
      // pause_start_date regardless of whether it's in the past.
      resume_date: '',
      cancel_date: '',
      reason: '',
      override_notice_period: false,
      override_credit: false,
    });
    
    setEnrollmentActionDialog(true);
    
    // Auto-calculate credit for cancel
    if (action === 'cancel') {
      calculateCancelCredit(enrollment.id);
    }
  };

  const calculatePauseCredit = async () => {
    if (!selectedEnrollment || !actionForm.pause_start_date) return;
    
    setCalculatingCredit(true);
    try {
      const response = await api.get(`/enrollments/${selectedEnrollment.id}/calculate-pause-credit?pause_date=${actionForm.pause_start_date}`);
      setCreditCalculation(response.data);
    } catch (error) {
      console.error('Failed to calculate credit:', error);
      toast.error('Failed to calculate credit');
    } finally {
      setCalculatingCredit(false);
    }
  };

  // iter334ba — Auto-fetch pause credit whenever pause_start_date
  // changes on the pause modal (debounced). Backdated pauses on paid
  // months surface the credit preview without a manual button click.
  useEffect(() => {
    if (enrollmentAction !== 'pause') return;
    if (!selectedEnrollment || !actionForm.pause_start_date) {
      setCreditCalculation(null);
      return;
    }
    const t = setTimeout(() => {
      calculatePauseCredit();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionForm.pause_start_date, enrollmentAction, selectedEnrollment?.id]);

  const calculateCancelCredit = async (enrollmentId, cancelDate = null) => {
    setCalculatingCredit(true);
    try {
      let url = `/enrollments/${enrollmentId || selectedEnrollment.id}/calculate-cancel-credit`;
      if (cancelDate) {
        url += `?cancel_date=${cancelDate}`;
      }
      const response = await api.get(url);
      setCreditCalculation(response.data);
      
      // Set the earliest cancel date as default if not set
      if (!actionForm.cancel_date) {
        setActionForm(prev => ({
          ...prev,
          cancel_date: response.data.earliest_cancel_date
        }));
      }
    } catch (error) {
      console.error('Failed to calculate credit:', error);
      toast.error('Failed to calculate credit');
    } finally {
      setCalculatingCredit(false);
    }
  };

  const handleEnrollmentAction = async () => {
    if (!selectedEnrollment) return;
    
    setProcessingAction(true);
    try {
      let response;
      
      if (enrollmentAction === 'pause') {
        if (!actionForm.pause_start_date) {
          toast.error('Please provide a pause start date');
          setProcessingAction(false);
          return;
        }
        // iter334au — Resume date is now optional (indefinite pause).
        // Send null when the admin leaves it blank; the backend
        // handles the null-NBD stash.
        response = await api.post(`/enrollments/${selectedEnrollment.id}/admin-pause`, {
          pause_start_date: actionForm.pause_start_date,
          resume_date: actionForm.resume_date || null,
          reason: actionForm.reason,
          // iter334bb — Admin opts to skip the pro-rated credit.
          override_credit: !!actionForm.override_credit,
        });
      } else if (enrollmentAction === 'cancel') {
        // Reason is now mandatory — block the submit before hitting
        // the backend so the admin gets immediate UI feedback.
        if (!(actionForm.reason || '').trim()) {
          toast.error('Reason is required to cancel an enrollment');
          setProcessingAction(false);
          return;
        }
        response = await api.post(`/enrollments/${selectedEnrollment.id}/admin-cancel`, {
          cancel_date: actionForm.cancel_date || null,
          reason: actionForm.reason,
          override_notice_period: !!actionForm.override_notice_period,
          override_credit: !!actionForm.override_credit,
        });
      } else if (enrollmentAction === 'resume') {
        response = await api.post(`/enrollments/${selectedEnrollment.id}/admin-resume`, {
          resume_date: actionForm.resume_date || null,
          reason: actionForm.reason
        });
      }
      
      toast.success(response.data.message || 'Action completed successfully');
      setEnrollmentActionDialog(false);
      fetchPlayerDetails(); // Refresh data
      
    } catch (error) {
      console.error('Failed to process action:', error);
      toast.error(error.response?.data?.detail || 'Failed to process action');
    } finally {
      setProcessingAction(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Player Details">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!player) {
    return (
      <DashboardLayout title="Player Details">
        <div className="text-center py-12">
          <p className="text-slate-500">Player not found</p>
          <Button onClick={() => navigate('/admin/players')} className="mt-4">
            Back to Players
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Player Details">
      <div className="space-y-6" data-testid="player-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/admin/players')}
              data-testid="back-to-players"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-bold text-xl">
                  {player.full_name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl lg:text-2xl font-bold text-slate-900">{player.full_name}</h1>
                  {player.numeric_id && (
                    <Badge variant="outline" className="text-xs font-mono bg-slate-100">
                      Player ID: {player.numeric_id}
                    </Badge>
                  )}
                </div>
                <p className="text-slate-500">
                  {player.age} years old • {player.skill_level || 'Beginner'}
                </p>
              </div>
            </div>
          </div>
          <Button onClick={() => setEditDialog(true)} data-testid="edit-player-btn">
            <Pencil className="h-4 w-4 mr-2" />
            Edit Player
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex w-full max-w-3xl overflow-x-auto">
            <TabsTrigger value="overview" data-testid="tab-overview" className="text-xs lg:text-sm whitespace-nowrap">Overview</TabsTrigger>
            <TabsTrigger value="enrollments" data-testid="tab-enrollments" className="text-xs lg:text-sm whitespace-nowrap">Enrollments</TabsTrigger>
            <TabsTrigger value="attendance" data-testid="tab-attendance" className="text-xs lg:text-sm whitespace-nowrap">Attendance</TabsTrigger>
            <TabsTrigger value="tournaments" data-testid="tab-tournaments" className="text-xs lg:text-sm whitespace-nowrap">Tournaments</TabsTrigger>
            <TabsTrigger value="games" data-testid="tab-games" className="text-xs lg:text-sm whitespace-nowrap">Games</TabsTrigger>
            <TabsTrigger value="evaluations" data-testid="tab-evaluations" className="text-xs lg:text-sm whitespace-nowrap">Evaluations</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes" className="text-xs lg:text-sm whitespace-nowrap">Notes</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Basic Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Full Name</p>
                      <p className="font-medium">{player.full_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Date of Birth</p>
                      <p className="font-medium">
                        {player.date_of_birth ? new Date(player.date_of_birth + 'T00:00:00').toLocaleDateString() : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Age</p>
                      <p className="font-medium">{player.age} years</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Gender</p>
                      <p className="font-medium capitalize">{player.gender || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Skill Level</p>
                      <Badge className="bg-blue-100 text-blue-700 capitalize">
                        {player.skill_level || 'Beginner'}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Parent Links */}
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-slate-500">
                        Parent{(player.parent_ids?.length > 1 || (player.parent_ids?.length === 1 && player.parent_id && !player.parent_ids.includes(player.parent_id))) ? 's' : ''}
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={openAddParentDialog}
                        data-testid="add-parent-btn"
                      >
                        <UserPlus className="h-3 w-3 mr-1" />
                        Add Parent
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {/* Primary parent */}
                      {player.parent_id && (
                        <div className="flex items-center justify-between group">
                          <Link 
                            to={`/admin/parents/${player.parent_id}`}
                            className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                            data-testid="parent-link"
                          >
                            <Users className="h-4 w-4" />
                            {player.parent_name}
                            <Badge variant="outline" className="text-xs">Primary</Badge>
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setUnlinkParentDialog(true)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 ml-2"
                            data-testid="unlink-primary-parent-btn"
                            title="Unlink primary parent"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      {/* Additional parents from parent_ids */}
                      {player.parent_ids?.filter(pid => pid !== player.parent_id).map((parentId, idx) => {
                        const parentName = player.additional_parents?.find(p => p.id === parentId)?.name || `Parent ${idx + 2}`;
                        return (
                          <div key={parentId} className="flex items-center justify-between group">
                            <Link
                              to={`/admin/parents/${parentId}`}
                              className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                              data-testid={`parent-link-${idx}`}
                            >
                              <Users className="h-4 w-4" />
                              {parentName}
                              <ExternalLink className="h-3 w-3" />
                            </Link>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMakePrimary(parentId, parentName)}
                                disabled={promotingParent === parentId}
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 text-xs"
                                data-testid={`make-primary-${parentId}`}
                                title="Make this parent the primary"
                              >
                                {promotingParent === parentId ? <span className="animate-spin">⏳</span> : 'Make Primary'}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveParent(parentId)}
                                disabled={removingParent === parentId}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`remove-parent-${parentId}`}
                                title="Remove this parent link"
                              >
                                {removingParent === parentId ? <span className="animate-spin">⏳</span> : <X className="h-4 w-4" />}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {player.medical_notes && (
                    <div className="pt-4 border-t">
                      <p className="text-sm text-slate-500 mb-2">Medical Notes</p>
                      <p className="text-sm bg-amber-50 p-3 rounded-lg text-amber-800">
                        {player.medical_notes}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Contact & External IDs */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5" />
                    Contact & External IDs
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {player.player_mobile && (
                      <div>
                        <p className="text-sm text-slate-500">Mobile</p>
                        <a href={`tel:${player.player_mobile}`} className="font-medium text-blue-600 hover:underline">
                          {player.player_mobile}
                        </a>
                      </div>
                    )}
                    {player.player_email && (
                      <div>
                        <p className="text-sm text-slate-500">Email</p>
                        <a href={`mailto:${player.player_email}`} className="font-medium text-blue-600 hover:underline text-sm">
                          {player.player_email}
                        </a>
                      </div>
                    )}
                    {player.cricclubs_id && (
                      <div>
                        <p className="text-sm text-slate-500">CricClubs ID</p>
                        <p className="font-medium">{player.cricclubs_id}</p>
                      </div>
                    )}
                    {player.cricclubs_email && (
                      <div>
                        <p className="text-sm text-slate-500">CricClubs Email</p>
                        <p className="font-medium text-sm">{player.cricclubs_email}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Uniform Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shirt className="h-5 w-5" />
                    Uniform Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Shirt Name</p>
                      <p className="font-medium">{player.shirt_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Shirt Number</p>
                      <p className="font-medium">{player.shirt_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Chest Size</p>
                      <p className="font-medium">{player.chest_size || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Shirt Length</p>
                      <p className="font-medium">{player.shirt_length || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Sleeves</p>
                      <p className="font-medium">{player.sleeves || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Waist Size</p>
                      <p className="font-medium">{player.waist_size || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Trouser Length</p>
                      <p className="font-medium">{player.trouser_length || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Attendance Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Attendance Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {player.attendance_summary ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-3xl font-bold text-slate-900">
                          {player.attendance_summary.attendance_rate || 0}%
                        </p>
                        <p className="text-sm text-slate-500">Attendance Rate</p>
                      </div>
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-3xl font-bold text-slate-900">
                          {player.attendance_summary.total || 0}
                        </p>
                        <p className="text-sm text-slate-500">Total Classes</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">
                          {player.attendance_summary.present || 0}
                        </p>
                        <p className="text-sm text-green-600">Present</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <p className="text-2xl font-bold text-red-600">
                          {player.attendance_summary.absent || 0}
                        </p>
                        <p className="text-sm text-red-600">Absent</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">No attendance data</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Enrollments Tab */}
          <TabsContent value="enrollments" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5" />
                  Enrollment History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {player.enrollments && player.enrollments.length > 0 ? (
                  <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Enrollment ID</TableHead>
                        <TableHead>Program</TableHead>
                        <TableHead>Class</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Start</TableHead>
                        <TableHead>End</TableHead>
                        <TableHead>Fee</TableHead>
                        <TableHead>Frequency</TableHead>
                        <TableHead>Next Bill</TableHead>
                        <TableHead>Last Paid</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {player.enrollments.map((enrollment) => {
                        const hasEndDate = enrollment.cancel_date || enrollment.end_date;
                        const isPast = hasEndDate && ['cancelled', 'canceled', 'completed'].includes(enrollment.status?.toLowerCase());
                        const canPause = ['current', 'active', 'approved'].includes(enrollment.status?.toLowerCase());
                        const canResume = enrollment.status?.toLowerCase() === 'paused';
                        const canCancel = ['current', 'active', 'approved', 'paused'].includes(enrollment.status?.toLowerCase());
                        const freq = enrollment.payment_frequency || 'monthly';
                        const freqLabel = freq === 'quarterly' ? 'Quarterly' : freq === 'biannual' ? 'Bi-Annual' : 'Monthly';
                        const freqMult = freq === 'quarterly' ? 3 : freq === 'biannual' ? 6 : 1;

                        return (
                          <TableRow key={enrollment.id} className={isPast ? 'opacity-60' : ''} data-testid={`enrollment-row-${enrollment.id}`}>
                            {/* iter302: enrollment id link — parity with ParentDetail. */}
                            <TableCell className="font-mono text-xs">
                              <Link
                                to={`/admin/enrollments/${enrollment.id}`}
                                className="text-blue-600 hover:underline"
                                data-testid={`enrollment-id-link-${enrollment.id}`}
                                title={enrollment.id}
                              >
                                {enrollment.numeric_id || (enrollment.id ? enrollment.id.slice(0, 8) : '-')}
                              </Link>
                            </TableCell>
                            <TableCell className="font-medium">
                              {enrollment.program_name}
                            </TableCell>
                            <TableCell>
                              {(enrollment.class_names && enrollment.class_names.length > 0) ? (
                                <div className="flex flex-wrap gap-1" data-testid={`enrollment-classes-${enrollment.id}`}>
                                  {enrollment.class_names.map((name, idx) => (
                                    <Badge
                                      key={(enrollment.class_ids && enrollment.class_ids[idx]) || `${enrollment.id}-${idx}`}
                                      variant="outline"
                                      className="text-xs"
                                    >
                                      {name}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                enrollment.class_name || '-'
                              )}
                            </TableCell>
                            <TableCell>
                              {isPast ? (
                                <Badge className="bg-slate-100 text-slate-600">
                                  Past
                                </Badge>
                              ) : (
                                getEnrollmentStatusBadge(enrollment.status)
                              )}
                            </TableCell>
                            <TableCell>
                              {enrollment.start_date ? formatDateOnly(enrollment.start_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' }) : '-'}
                            </TableCell>
                            <TableCell>
                              {hasEndDate ? (
                                <span className="text-slate-600">
                                  {formatDateOnly(enrollment.cancel_date || enrollment.end_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })}
                                </span>
                              ) : '-'}
                            </TableCell>
                            <TableCell data-testid={`enr-fee-${enrollment.id}`}>
                              {enrollment.monthly_fee != null ? `${cs()}${Number(enrollment.monthly_fee).toFixed(2)}` : '-'}
                              {enrollment.credit_balance > 0 && (
                                <div className="text-xs text-green-600">
                                  Credit: {cs()}{enrollment.credit_balance.toFixed(2)}
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">{freqLabel}</span>
                              {freqMult > 1 && (
                                <span className="text-[10px] text-slate-400 block">× {freqMult} months</span>
                              )}
                            </TableCell>
                            <TableCell data-testid={`enr-next-bill-${enrollment.id}`}>
                              {enrollment.next_billing_date
                                ? formatDateOnly(enrollment.next_billing_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })
                                : '-'}
                            </TableCell>
                            <TableCell data-testid={`enr-last-paid-${enrollment.id}`}>
                              {enrollment.last_payment_date ? (
                                <>
                                  <span className="text-sm">
                                    {formatDateOnly(enrollment.last_payment_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })}
                                  </span>
                                  {enrollment.last_payment_amount != null && (
                                    <span className="text-[10px] text-slate-400 block">
                                      {cs()}{Number(enrollment.last_payment_amount).toFixed(2)}
                                    </span>
                                  )}
                                </>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              {!isPast && (
                                <div className="flex items-center justify-end gap-1">
                                  {canPause && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openEnrollmentAction(enrollment, 'pause')}
                                      className="text-amber-600 hover:text-amber-700"
                                      title="Pause Enrollment"
                                    >
                                      <PauseCircle className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {canResume && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openEnrollmentAction(enrollment, 'resume')}
                                      className="text-green-600 hover:text-green-700"
                                      title="Resume Enrollment"
                                    >
                                      <PlayCircle className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {canCancel && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openEnrollmentAction(enrollment, 'cancel')}
                                      className="text-red-600 hover:text-red-700"
                                      title="Cancel Enrollment"
                                    >
                                      <XCircle className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <GraduationCap className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No enrollments yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Attendance Tab */}
          <TabsContent value="attendance" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Attendance History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {player.attendance && player.attendance.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Class</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {player.attendance.map((record, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            {new Date(record.date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>{record.class_name || '-'}</TableCell>
                          <TableCell>{getStatusBadge(record.status)}</TableCell>
                          <TableCell className="text-slate-500">
                            {record.notes || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No attendance records yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tournaments Tab */}
          <TabsContent value="tournaments" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-4">
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="h-5 w-5" />
                    Tournament History
                  </CardTitle>
                  <Button
                    size="sm"
                    onClick={openRegisterTournamentDialog}
                    data-testid="register-for-tournament-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Register for Tournament
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingTournaments ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
                  </div>
                ) : tournamentHistory.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tournament</TableHead>
                        <TableHead>Year</TableHead>
                        <TableHead>Dates</TableHead>
                        <TableHead>Age Category</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tournamentHistory.map((tournament) => (
                        <TableRow key={tournament.id}>
                          <TableCell>
                            <Link 
                              to={`/admin/tournaments/${tournament.tournament_id}`}
                              className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                            >
                              {tournament.tournament_name}
                            </Link>
                            <p className="text-xs text-slate-500 capitalize">
                              {tournament.location_type === 'local' ? 'Local' : 'Out of State'}
                            </p>
                          </TableCell>
                          <TableCell>{tournament.tournament_year}</TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {tournament.tournament_dates}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="uppercase">
                              {tournament.age_category}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              tournament.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                              tournament.payment_status === 'processing' ? 'bg-blue-100 text-blue-700' :
                              tournament.payment_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }>
                              {tournament.payment_status === 'paid' ? 'Paid' :
                               tournament.payment_status === 'processing' ? 'Processing' :
                               tournament.payment_status === 'pending' ? 'Pending' : 'Unpaid'}
                            </Badge>
                            {tournament.payment_amount > 0 && (
                              <p className="text-xs text-slate-500 mt-1">
                                <NetAmount
                                  amount={tournament.payment_amount}
                                  refundedAmount={tournament.refunded_amount}
                                  refundStatus={tournament.refund_status}
                                  testId={`player-tournament-net-${tournament.id}`}
                                />
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              tournament.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                              tournament.status === 'selected' ? 'bg-blue-100 text-blue-700' :
                              tournament.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }>
                              {tournament.status?.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Trophy className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No tournament history yet</p>
                    <p className="text-sm mt-2">Tournament participation will appear here once the player is registered.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Games Tab */}
          <TabsContent value="games" className="mt-6">
            <PlayerGamesView
              playerId={playerId}
              tournamentLink={(tid) => `/admin/tournaments/${tid}`}
            />
          </TabsContent>

          {/* Evaluations Tab */}
          <TabsContent value="evaluations" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Performance Evaluations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-slate-500">
                  <Activity className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>Performance evaluations coming soon</p>
                  <p className="text-sm mt-2">This section will show the player's evaluation history and skill progression.</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes" className="mt-6">
            <PlayerNotesSection playerId={playerId} />
          </TabsContent>
        </Tabs>

        {/* Edit Player Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Player</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input
                  value={playerForm.full_name}
                  onChange={(e) => setPlayerForm({ ...playerForm, full_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Date of Birth</Label>
                <Input
                  type="date"
                  value={playerForm.date_of_birth}
                  onChange={(e) => setPlayerForm({ ...playerForm, date_of_birth: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Gender</Label>
                <Select value={playerForm.gender} onValueChange={(v) => setPlayerForm({ ...playerForm, gender: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Skill Level</Label>
                <Select value={playerForm.skill_level} onValueChange={(v) => setPlayerForm({ ...playerForm, skill_level: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="col-span-2 border-t pt-4 mt-2">
                <h4 className="font-medium mb-3">Contact Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Player Mobile</Label>
                    <Input
                      value={playerForm.player_mobile}
                      onChange={(e) => setPlayerForm({ ...playerForm, player_mobile: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Player Email</Label>
                    <Input
                      type="email"
                      value={playerForm.player_email}
                      onChange={(e) => setPlayerForm({ ...playerForm, player_email: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              
              <div className="col-span-2 border-t pt-4 mt-2">
                <h4 className="font-medium mb-3">Uniform Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Shirt Name</Label>
                    <Input
                      value={playerForm.shirt_name}
                      onChange={(e) => setPlayerForm({ ...playerForm, shirt_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Shirt Number</Label>
                    <Input
                      value={playerForm.shirt_number}
                      onChange={(e) => setPlayerForm({ ...playerForm, shirt_number: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Chest Size</Label>
                    <Input
                      value={playerForm.chest_size}
                      onChange={(e) => setPlayerForm({ ...playerForm, chest_size: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Shirt Length</Label>
                    <Input
                      value={playerForm.shirt_length}
                      onChange={(e) => setPlayerForm({ ...playerForm, shirt_length: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sleeves</Label>
                    <Input
                      value={playerForm.sleeves}
                      onChange={(e) => setPlayerForm({ ...playerForm, sleeves: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Waist Size</Label>
                    <Input
                      value={playerForm.waist_size}
                      onChange={(e) => setPlayerForm({ ...playerForm, waist_size: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Trouser Length</Label>
                    <Input
                      value={playerForm.trouser_length}
                      onChange={(e) => setPlayerForm({ ...playerForm, trouser_length: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              
              <div className="col-span-2 border-t pt-4 mt-2">
                <h4 className="font-medium mb-3">External IDs</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>CricClubs ID</Label>
                    <Input
                      value={playerForm.cricclubs_id}
                      onChange={(e) => setPlayerForm({ ...playerForm, cricclubs_id: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>CricClubs Email</Label>
                    <Input
                      type="email"
                      value={playerForm.cricclubs_email}
                      onChange={(e) => setPlayerForm({ ...playerForm, cricclubs_email: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              
              <div className="col-span-2 space-y-2">
                <Label>Medical Notes</Label>
                <Textarea
                  value={playerForm.medical_notes}
                  onChange={(e) => setPlayerForm({ ...playerForm, medical_notes: e.target.value })}
                  placeholder="Any medical conditions or allergies..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSavePlayer} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Enrollment Action Dialog */}
        <Dialog open={enrollmentActionDialog} onOpenChange={setEnrollmentActionDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {enrollmentAction === 'pause' && <PauseCircle className="h-5 w-5 text-amber-500" />}
                {enrollmentAction === 'cancel' && <XCircle className="h-5 w-5 text-red-500" />}
                {enrollmentAction === 'resume' && <PlayCircle className="h-5 w-5 text-green-500" />}
                {enrollmentAction === 'pause' && 'Pause Enrollment'}
                {enrollmentAction === 'cancel' && 'Cancel Enrollment'}
                {enrollmentAction === 'resume' && 'Resume Enrollment'}
              </DialogTitle>
            </DialogHeader>
            
            {selectedEnrollment && (
              <div className="space-y-4">
                {/* Enrollment Info */}
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium">{selectedEnrollment.program_name}</p>
                  <p className="text-sm text-slate-500">{selectedEnrollment.class_name}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span>Fee: {cs()}{selectedEnrollment.monthly_fee}/mo</span>
                    <span className="capitalize">({selectedEnrollment.payment_frequency})</span>
                  </div>
                  {selectedEnrollment.credit_balance > 0 && (
                    <p className="text-sm text-green-600 mt-1">
                      Existing Credit: {cs()}{selectedEnrollment.credit_balance.toFixed(2)}
                    </p>
                  )}
                </div>
                
                {/* Pause Form */}
                {enrollmentAction === 'pause' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Pause Start Date *</Label>
                        <Input
                          type="date"
                          value={actionForm.pause_start_date}
                          onChange={(e) => {
                            setActionForm({ ...actionForm, pause_start_date: e.target.value });
                            setCreditCalculation(null);
                          }}
                          data-testid="pause-start-date-input"
                        />
                        {/* iter334az — Backdating is intentionally allowed.
                            When the pause_start_date is in the past AND
                            the parent has already paid for the current
                            cycle, `calculatePauseCredit` (existing flow)
                            computes the credit to issue based on the
                            unused portion from the pause start. Admins
                            can override the credit via the checkbox below. */}
                        <p className="text-[11px] text-slate-500">
                          Backdate allowed — credit for any unused portion is calculated below.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Resume Date <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
                        <Input
                          type="date"
                          value={actionForm.resume_date}
                          onChange={(e) => setActionForm({ ...actionForm, resume_date: e.target.value })}
                          min={actionForm.pause_start_date}
                          data-testid="pause-resume-date-input"
                        />
                        {/* iter334au — Admins can leave this blank for an
                            indefinite pause (medical leave, etc.). Bring
                            the enrollment back later via "Resume". */}
                        <p className="text-[11px] text-slate-500">
                          Leave blank to pause indefinitely — use <strong>Resume</strong> later to bring the enrollment back.
                        </p>
                      </div>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={calculatePauseCredit}
                      disabled={calculatingCredit || !actionForm.pause_start_date}
                    >
                      {calculatingCredit ? 'Calculating...' : 'Calculate Credit'}
                    </Button>

                    {/* iter334bb — Zero-credit override for pause (mirrors
                        the cancel-flow checkbox). Only rendered when the
                        calculated credit is > 0 so an "empty" pause
                        (backdated onto an unpaid month, or end-of-cycle)
                        doesn't show a misleading control. */}
                    {creditCalculation && (creditCalculation.credit_amount || 0) > 0 && (
                      <label
                        className="flex items-start gap-2 p-3 rounded-lg border bg-slate-50 border-slate-200 cursor-pointer"
                        data-testid="pause-zero-credit-block"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 text-slate-700 rounded"
                          checked={!!actionForm.override_credit}
                          onChange={(e) =>
                            setActionForm({ ...actionForm, override_credit: e.target.checked })
                          }
                          data-testid="pause-zero-credit-checkbox"
                        />
                        <span className="text-sm text-slate-700">
                          <span className="font-medium">Do not issue credit (zero out the {cs()}{creditCalculation.credit_amount.toFixed(2)} above)</span>
                          <span className="block text-xs text-slate-500 mt-0.5">
                            Use this when the pause is mutually agreed and no refund / account credit should be issued.
                          </span>
                        </span>
                      </label>
                    )}
                  </div>
                )}
                
                {/* Cancel Form */}
                {enrollmentAction === 'cancel' && (
                  <div className="space-y-4">
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-amber-800">Notice Period Required</p>
                          <p className="text-amber-700">
                            Cancellation requires 1 billing cycle notice (minimum 30 days).
                            {creditCalculation?.earliest_cancel_date && (
                              <span className="block mt-1">
                                Earliest cancel date: <strong>{formatDateOnly(creditCalculation.earliest_cancel_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })}</strong>
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Cancel Date</Label>
                      <Input
                        type="date"
                        value={actionForm.cancel_date}
                        onChange={(e) => {
                          setActionForm({ ...actionForm, cancel_date: e.target.value });
                          if (e.target.value) {
                            calculateCancelCredit(selectedEnrollment.id, e.target.value);
                          }
                        }}
                        data-testid="cancel-date-input"
                      />
                      <p className="text-xs text-slate-500">
                        Admin can pick any date — leave empty to use the earliest allowed date.
                      </p>
                    </div>

                    {/* Early-cancel warning + admin override checkbox.
                        Shown only when the picked date is before the
                        system-calculated earliest cancel date. */}
                    {actionForm.cancel_date && creditCalculation?.earliest_cancel_date && (
                      new Date(actionForm.cancel_date) < new Date(creditCalculation.earliest_cancel_date)
                    ) && (
                      <div
                        className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-2"
                        data-testid="early-cancel-warning"
                      >
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-red-800">
                            <p className="font-medium">Cancelling BEFORE the notice period</p>
                            <p className="text-red-700 mt-1">
                              The selected date is earlier than the 30-day notice + current cycle.
                              This bypasses the standard cancellation policy and is logged in the audit trail.
                            </p>
                          </div>
                        </div>
                        <label className="flex items-center gap-2 text-sm text-red-900 pl-7 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!actionForm.override_notice_period}
                            onChange={(e) =>
                              setActionForm({ ...actionForm, override_notice_period: e.target.checked })
                            }
                            data-testid="override-notice-checkbox"
                          />
                          <span>I confirm overriding the notice period (admin)</span>
                        </label>
                      </div>
                    )}

                    {/* Zero-credit override — admin can opt out of issuing
                        the calculated pro-rated credit (e.g. mutually
                        agreed cancellation with no refund). */}
                    {creditCalculation && (creditCalculation.credit_amount || 0) > 0 && (
                      <label
                        className="flex items-start gap-2 p-3 rounded-lg border bg-slate-50 border-slate-200 cursor-pointer"
                        data-testid="zero-credit-block"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 text-slate-700 rounded"
                          checked={!!actionForm.override_credit}
                          onChange={(e) =>
                            setActionForm({ ...actionForm, override_credit: e.target.checked })
                          }
                          data-testid="zero-credit-checkbox"
                        />
                        <span className="text-sm text-slate-700">
                          <span className="font-medium">Do not issue credit (zero out the {cs()}{creditCalculation.credit_amount.toFixed(2)} above)</span>
                          <span className="block text-xs text-slate-500 mt-0.5">
                            Use this when the cancellation is mutually agreed and no refund / account credit should be issued.
                          </span>
                        </span>
                      </label>
                    )}
                  </div>
                )}
                
                {/* Resume Form */}
                {enrollmentAction === 'resume' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Resume Date</Label>
                      <Input
                        type="date"
                        value={actionForm.resume_date}
                        onChange={(e) => setActionForm({ ...actionForm, resume_date: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                      />
                      <p className="text-xs text-slate-500">
                        Leave empty to resume immediately
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Credit Calculation Display */}
                {creditCalculation && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <DollarSign className="h-5 w-5 text-blue-500 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-blue-800">Credit Calculation</p>
                        <div className="text-sm text-blue-700 mt-1 space-y-1">
                          <p>Billing Period: {new Date(creditCalculation.billing_period_start).toLocaleDateString()} - {new Date(creditCalculation.billing_period_end).toLocaleDateString()}</p>
                          <p>Days Remaining: {creditCalculation.days_remaining} of {creditCalculation.days_in_period}</p>
                          <p className="font-medium text-lg">
                            Credit: {cs()}{creditCalculation.credit_amount?.toFixed(2) || '0.00'}
                          </p>
                          {creditCalculation.is_end_of_cycle && (
                            <p className="text-green-600">✓ At end of billing cycle - no credit needed</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Reason — mandatory on Cancel, optional on Pause/Resume. */}
                <div className="space-y-2">
                  <Label>
                    Reason
                    {enrollmentAction === 'cancel' ? (
                      <span className="text-red-500 ml-1">*</span>
                    ) : (
                      <span className="text-slate-400 text-xs"> (Optional)</span>
                    )}
                  </Label>
                  <Textarea
                    value={actionForm.reason}
                    onChange={(e) => setActionForm({ ...actionForm, reason: e.target.value })}
                    placeholder={enrollmentAction === 'cancel'
                      ? 'Required — record why this enrollment is being cancelled.'
                      : 'Enter reason for this action...'}
                    rows={2}
                    required={enrollmentAction === 'cancel'}
                    data-testid="enrollment-action-reason"
                  />
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEnrollmentActionDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleEnrollmentAction}
                disabled={
                  processingAction ||
                  // For cancel: reason required, and if user picked an
                  // early cancel date they must tick the override.
                  (enrollmentAction === 'cancel' && (
                    !(actionForm.reason || '').trim() ||
                    (
                      actionForm.cancel_date &&
                      creditCalculation?.earliest_cancel_date &&
                      new Date(actionForm.cancel_date) < new Date(creditCalculation.earliest_cancel_date) &&
                      !actionForm.override_notice_period
                    )
                  ))
                }
                className={
                  enrollmentAction === 'cancel' ? 'bg-red-600 hover:bg-red-700' :
                  enrollmentAction === 'pause' ? 'bg-amber-600 hover:bg-amber-700' :
                  'bg-green-600 hover:bg-green-700'
                }
                data-testid="confirm-enrollment-action-btn"
              >
                {processingAction ? 'Processing...' : 
                  enrollmentAction === 'pause' ? 'Pause Enrollment' :
                  enrollmentAction === 'cancel' ? 'Cancel Enrollment' :
                  'Resume Enrollment'
                }
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Parent Dialog */}
        <Dialog open={addParentDialog} onOpenChange={setAddParentDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-blue-600" />
                Add Guardian to {player?.full_name}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search any user — name, email, phone, ID…"
                  value={parentSearch}
                  onChange={(e) => setParentSearch(e.target.value)}
                  className="pl-9"
                  data-testid="parent-search-input"
                />
              </div>
              <p className="text-xs text-slate-500">
                Any user role can be linked (parent, customer, coach, staff, admin).
                <strong> Customers are auto-promoted to parents</strong> when linked.
              </p>

              <div className="max-h-72 overflow-y-auto border rounded-lg">
                {getFilteredParents().length === 0 ? (
                  <div className="p-4 text-center text-slate-500 text-sm">
                    {parentSearch.length < 2
                      ? 'Type at least 2 characters to search'
                      : 'No matching users'}
                  </div>
                ) : (
                  getFilteredParents().map(user => (
                    <div
                      key={user.id}
                      className="p-3 border-b last:border-b-0 hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                      onClick={() => !addingParent && handleAddParent(user.id)}
                      data-testid={`select-parent-${user.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium truncate">{user.name || '(no name)'}</p>
                          {user.role && (
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide ${
                              user.role === 'customer' ? 'bg-orange-100 text-orange-700'
                              : user.role === 'parent' ? 'bg-blue-100 text-blue-700'
                              : user.role === 'admin' ? 'bg-rose-100 text-rose-700'
                              : user.role === 'staff' ? 'bg-amber-100 text-amber-700'
                              : user.role === 'coach' ? 'bg-purple-100 text-purple-700'
                              : 'bg-slate-100 text-slate-700'
                            }`}>
                              {user.role}
                            </span>
                          )}
                          {user.numeric_id && (
                            <span className="text-[10px] text-slate-500 font-mono bg-slate-100 px-1 rounded">
                              {user.numeric_id}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 truncate">{user.email || '—'}</p>
                        {user.phone && (
                          <p className="text-xs text-slate-400">{user.phone}</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={addingParent}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddParent(user.id);
                        }}
                      >
                        {addingParent ? 'Adding...' : 'Add'}
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setAddParentDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Unlink Primary Parent Confirmation Dialog */}
        <Dialog open={unlinkParentDialog} onOpenChange={setUnlinkParentDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Unlink Primary Parent
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-slate-600 mb-4">
                Are you sure you want to unlink <strong>{player?.parent_name}</strong> from <strong>{player?.full_name}</strong>?
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <p className="font-medium mb-1">Warning:</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li>The parent will no longer be able to manage this player</li>
                  <li>Enrollment and billing information will remain</li>
                  <li>You can re-link a parent later using "Add Parent"</li>
                </ul>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => setUnlinkParentDialog(false)}
                disabled={unlinkingParent}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleUnlinkPrimaryParent}
                disabled={unlinkingParent}
                className="bg-red-600 hover:bg-red-700"
              >
                {unlinkingParent ? 'Unlinking...' : 'Unlink Parent'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Register for Tournament Dialog */}
        <Dialog open={registerTournamentDialog} onOpenChange={setRegisterTournamentDialog}>
          <DialogContent className="sm:max-w-lg" data-testid="register-for-tournament-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-emerald-600" />
                Register {player?.full_name} for Tournament
              </DialogTitle>
              <DialogDescription>
                Select an open tournament and age category to register this player.
                Parent consent is recorded on the parent's behalf.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Tournament</Label>
                {loadingActiveTournaments ? (
                  <div className="text-sm text-slate-500">Loading tournaments…</div>
                ) : activeTournaments.length === 0 ? (
                  <div className="text-sm text-slate-500" data-testid="no-active-tournaments-msg">
                    No open tournaments available.
                  </div>
                ) : (
                  <Select value={selectedTournamentId} onValueChange={handleTournamentChange}>
                    <SelectTrigger data-testid="register-tournament-select">
                      <SelectValue placeholder="Choose a tournament" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeTournaments.map(t => (
                        <SelectItem key={t.id} value={t.id} data-testid={`register-tournament-option-${t.id}`}>
                          {t.name} · {t.start_date} ({t.status})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {selectedTournamentId && (() => {
                const selected = activeTournaments.find(t => t.id === selectedTournamentId);
                const cats = selected?.age_categories || [];
                return (
                  <div className="space-y-2">
                    <Label>Age Category</Label>
                    {cats.length === 0 ? (
                      <div className="text-sm text-slate-500">This tournament has no age categories configured.</div>
                    ) : (
                      <Select value={selectedAgeCategory} onValueChange={handleCategoryChange}>
                        <SelectTrigger data-testid="register-category-select">
                          <SelectValue placeholder="Choose an age category" />
                        </SelectTrigger>
                        <SelectContent>
                          {cats.map(cat => (
                            <SelectItem key={cat} value={cat} data-testid={`register-category-option-${cat}`}>
                              {cat.toUpperCase()}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                );
              })()}

              {checkingEligibility && (
                <div className="text-xs text-slate-500" data-testid="checking-eligibility-msg">
                  Checking eligibility…
                </div>
              )}

              {registrationEligibility && !registrationEligibility.is_eligible && (
                <div
                  className="border border-amber-300 bg-amber-50 rounded-md p-3 text-sm space-y-2"
                  data-testid="eligibility-warning-block"
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-800">Player is not eligible for this category</p>
                      <p className="text-amber-700 text-xs mt-1">
                        {registrationEligibility.warning || 'Player does not meet the age requirements.'}
                      </p>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-amber-900 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={overrideEligibility}
                      onChange={(e) => setOverrideEligibility(e.target.checked)}
                      data-testid="eligibility-override-checkbox"
                    />
                    <span className="text-sm">Override eligibility (admin discretion)</span>
                  </label>
                </div>
              )}

              {registrationEligibility && registrationEligibility.is_eligible && (
                <div
                  className="border border-emerald-200 bg-emerald-50 rounded-md p-2 text-xs text-emerald-800"
                  data-testid="eligibility-ok-block"
                >
                  <CheckCircle className="h-3.5 w-3.5 inline mr-1" />
                  Player is eligible for {selectedAgeCategory?.toUpperCase()}.
                </div>
              )}

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  value={registrationNotes}
                  onChange={(e) => setRegistrationNotes(e.target.value)}
                  placeholder="Any additional info for this registration…"
                  rows={2}
                  data-testid="register-notes-input"
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setRegisterTournamentDialog(false)}
                disabled={registeringForTournament}
                data-testid="register-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={submitTournamentRegistration}
                disabled={
                  registeringForTournament ||
                  !selectedTournamentId ||
                  !selectedAgeCategory ||
                  (registrationEligibility && !registrationEligibility.is_eligible && !overrideEligibility)
                }
                data-testid="register-submit-btn"
              >
                {registeringForTournament ? 'Registering…' : 'Register Player'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default PlayerDetail;
